import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress,
  Tabs, Tab, Divider, Chip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { TurnosService } from '../services/turnosService';
import type { Turno } from '../types/turnos';

const emptyTurno: Turno = {
  idTurno: 0, codigo: '', descripcion: '',
  horaInicio: '08:00:00', horaFin: '17:00:00',
  horaExtraDesde: null, toleranciaMinutos: 5,
  horaInicioRefrigerio: '13:00:00', horaFinRefrigerio: '14:00:00',
  duracionRefrigerio: '01:00:00', duracionMaximaUsoMaquina: null,
  esNocturno: false, hastaElSiguienteDia: false, horarioFinSemana: false,
  sinLimiteSalida: false, esHorarioAmanecida: false,
  noConsiderarTardanzaPrimerIngreso: false, esParaProduccion: false,
  esParaCierreDiarioTurno: false, activo: true,
};

const timeVal = (v: string | null) => v?.substring(0, 5) ?? '';
const toTime = (v: string) => v ? v + ':00' : null;

export default function TurnosPage() {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<Turno>(emptyTurno);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => { load(); }, []);
  const load = async () => { try { setTurnos(await TurnosService.getAll()); } catch { /* handled */ } };

  const handleSave = async () => {
    setLoading(true);
    try { await TurnosService.save(form); setDialog(false); await load(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar turno?')) return;
    try { await TurnosService.delete(id); await load(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'codigo', headerName: 'Código', width: 80 },
    { field: 'descripcion', headerName: 'Descripción', flex: 1, minWidth: 150 },
    { field: 'horaInicio', headerName: 'Ingreso', width: 90, valueFormatter: (v: string) => v?.substring(0, 5) },
    { field: 'horaFin', headerName: 'Salida', width: 90, valueFormatter: (v: string) => v?.substring(0, 5) },
    { field: 'toleranciaMinutos', headerName: 'Toler.(min)', width: 100, type: 'number' },
    { field: 'esParaProduccion', headerName: 'Producción', width: 100, type: 'boolean' },
    { field: 'esNocturno', headerName: 'Nocturno', width: 90, type: 'boolean' },
    { field: 'activo', headerName: 'Activo', width: 70, type: 'boolean',
      renderCell: (p) => <Chip label={p.value ? 'Activo' : 'Inactivo'} size="small" color={p.value ? 'success' : 'default'} /> },
    { field: 'acciones', headerName: '', width: 120, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => { setForm(p.row); setTab(0); setDialog(true); }}><EditIcon fontSize="small" /></Button>
          <Button size="small" color="error" onClick={() => handleDelete(p.row.idTurno)}><DeleteIcon fontSize="small" /></Button>
        </Stack>
      )},
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Turnos de Trabajo</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(emptyTurno); setTab(0); setDialog(true); }}>Nuevo Turno</Button>
      </Stack>
      <DataGrid rows={turnos} columns={columns} getRowId={(row) => row.idTurno} autoHeight density="compact" disableRowSelectionOnClick pageSizeOptions={[10]} />

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.idTurno ? 'Editar Turno' : 'Nuevo Turno'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Header fields */}
            <Stack direction="row" spacing={2}>
              <TextField label="Código" value={form.codigo} sx={{ width: 120 }}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              <TextField label="Descripción" fullWidth value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              <FormControlLabel control={
                <Switch checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
              } label="Activo" />
            </Stack>

            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Características" />
              <Tab label="Horarios" />
            </Tabs>

            {/* Tab 0: Características */}
            {tab === 0 && (
              <Box sx={{ pl: 1 }}>
                <FormControlLabel control={
                  <Switch checked={form.hastaElSiguienteDia} onChange={(e) => setForm({ ...form, hastaElSiguienteDia: e.target.checked })} />
                } label="Hasta el siguiente día" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.horarioFinSemana} onChange={(e) => setForm({ ...form, horarioFinSemana: e.target.checked })} />
                } label="Horario de Fin de Semana" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.sinLimiteSalida} onChange={(e) => setForm({ ...form, sinLimiteSalida: e.target.checked })} />
                } label="Sin límite para la Salida" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.esHorarioAmanecida} onChange={(e) => setForm({ ...form, esHorarioAmanecida: e.target.checked })} />
                } label="Es horario de amanecida" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.noConsiderarTardanzaPrimerIngreso} onChange={(e) => setForm({ ...form, noConsiderarTardanzaPrimerIngreso: e.target.checked })} />
                } label="No considerar tardanza en el primer ingreso" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.esParaProduccion} onChange={(e) => setForm({ ...form, esParaProduccion: e.target.checked })} />
                } label="Es para producción" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.esParaCierreDiarioTurno} onChange={(e) => setForm({ ...form, esParaCierreDiarioTurno: e.target.checked })} />
                } label="Es para cierre diario/turno" />
                <br />
                <FormControlLabel control={
                  <Switch checked={form.esNocturno} onChange={(e) => setForm({ ...form, esNocturno: e.target.checked })} />
                } label="Turno Nocturno" />
              </Box>
            )}

            {/* Tab 1: Horarios */}
            {tab === 1 && (
              <Box>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField label="Hora de Ingreso" type="time" fullWidth value={timeVal(form.horaInicio)}
                    onChange={(e) => setForm({ ...form, horaInicio: e.target.value + ':00' })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                  <TextField label="Hora de Salida" type="time" fullWidth value={timeVal(form.horaFin)}
                    onChange={(e) => setForm({ ...form, horaFin: e.target.value + ':00' })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField label="Hora Extra desde" type="time" fullWidth value={timeVal(form.horaExtraDesde)}
                    onChange={(e) => setForm({ ...form, horaExtraDesde: toTime(e.target.value) })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                  <TextField label="Tolerancia (minutos)" type="number" fullWidth value={form.toleranciaMinutos}
                    onChange={(e) => setForm({ ...form, toleranciaMinutos: Number(e.target.value) })} />
                </Stack>
                <Divider sx={{ my: 2 }}><Typography variant="caption">Datos del Refrigerio</Typography></Divider>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField label="Desde" type="time" fullWidth value={timeVal(form.horaInicioRefrigerio)}
                    onChange={(e) => setForm({ ...form, horaInicioRefrigerio: toTime(e.target.value) })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                  <TextField label="Hasta" type="time" fullWidth value={timeVal(form.horaFinRefrigerio)}
                    onChange={(e) => setForm({ ...form, horaFinRefrigerio: toTime(e.target.value) })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField label="Duración del Refrigerio" type="time" fullWidth value={timeVal(form.duracionRefrigerio)}
                    onChange={(e) => setForm({ ...form, duracionRefrigerio: toTime(e.target.value) })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                </Stack>
                <Divider sx={{ my: 2 }}><Typography variant="caption">Uso de Máquina</Typography></Divider>
                <Stack direction="row" spacing={2}>
                  <TextField label="Duración Máxima de Uso de Máquina" type="time" fullWidth value={timeVal(form.duracionMaximaUsoMaquina)}
                    onChange={(e) => setForm({ ...form, duracionMaximaUsoMaquina: toTime(e.target.value) })}
                    slotProps={{ inputLabel: { shrink: true } }} />
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{loading ? <CircularProgress size={18} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
